// import type { TBaseRawLevelOptions } from './levelOptions'
import type {
  IChild,
  TSerial,
  TChildrenGrade,
  TAvailablePlatform,
  IPaginationRequest,
  IPaginationResponse,
  TModuleablePlatform,
  TExpandedAvailabilities
} from './common'

import type { TModuleLabels } from './labels'

/** Modulképek. */
export interface IBaseModuleImage {
  /** Az elsődleges szintopció azonosítója, ha van. */
  readonly levelOptionId: number | null
  /** Az app azonosítója. */
  readonly appId: number
  /** Az elérhetőségi beállítások. */
  readonly availabilityFlags: number
}

export type TBaseModuleImages = readonly IBaseModuleImage[]

export interface INewModuleImage extends IBaseModuleImage {
  readonly path: string
}

export interface IModuleImage extends INewModuleImage {
  readonly id: number
  readonly createdAt: string
}

export type TModuleImages = readonly IModuleImage[]

export interface IRandomModule {
  /** A modul azonosítója. */
  readonly id: number
  /** Annak az appnak a szériaszáma, amihez tartozik a modul. */
  readonly serial: TSerial
}

/** Random modulok listája. */
export type TRandomModules = readonly IRandomModule[]

/** A lehető legminimálisabb reprezentációja egy modulnak. */
export interface IMinimalModule {
  readonly id: number
  readonly appId: number
  readonly primaryLevelOptionId: number | null
}

export type TMinimalModules = readonly IMinimalModule[]

export interface IGetRandomModulesByThemeAndGradeResponse {
  /** A témához és évfolyamhoz véletlenszerűen kiválasztott modulok. */
  readonly modules: TRandomModules
  /** Ez most biztosan ugyanaz az azonosító lesz, amit a frontend felküld. */
  readonly themeLabelId: number
}

export interface IUpdateModule {
  /** A modul elérhetőségi flagje. */
  readonly availabilityFlags: number
  /** A modul elsődleges szintopciója, ha van neki. */
  readonly primaryLevelOptionId: number | null
}

export interface INewModule extends IUpdateModule {
  /** Az app amibe tartozik az adott modul. */
  readonly appId: number
}

// interface INewRawModule extends INewModule {
  // readonly levelOptionIds: readonly number[]
  // readonly levelOptions: TBaseRawLevelOptions
// }

export type TNewModules = INewModule[]

export interface IModule extends INewModule, IRandomModule {
  /** Az egyed létrehozásának dátuma. */
  readonly createdAt: string
  /** A modul címe az adott platformon. */
  readonly title: string
  /** A modul leírása az adott platformon. */
  readonly description: string
  /** Az modulhoz tartozó kép elérési útvonal az adott platformon. */
  readonly imagePath: string | null
}

export type TModules = readonly IModule[]

export interface IGetModuleByIdResponse {
  readonly module: IModule
  readonly isInRoute: boolean
}

export interface IBaseFavouriteModule {
  readonly moduleId: number
  readonly userId: string
}

export interface IFavouriteModule extends IBaseFavouriteModule {
  readonly createdAt: string
}

export type TFavouriteModules = readonly IFavouriteModule[]

/** Az alábbi hármas egyediséget kellene, hogy biztosítson. */
export interface IModuleTranslationIdentifier {
  /** Az app azonosítója. */
  readonly appId: number
  /** Az esetleges szintopció azonosítója. */
  readonly levelOptionId: number | null
  /** A platform, amelyen elérhető az adott fordítás. */
  readonly platform: TAvailablePlatform
}

export interface IIInsertModuleTranslation extends IModuleTranslationIdentifier {
  /** A module címe. */
  readonly title: string
  /** A modul leírása. */
  readonly description: string
}

export type TInsertModuleTranslations = readonly IIInsertModuleTranslation[]

export interface IModuleTranslation extends IIInsertModuleTranslation {
  /** Egyedi azonosító. */
  readonly id: number
  /** Mikor lett létrehozva. */
  readonly createdAt: string
  /** Módosítás dátuma, ha volt módosítva. */
  readonly updatedAt: string | null
}

export interface IAppDescriptionUpdateRequest {
  /** A kiválaszott modul azonosítója. */
  readonly moduleId: number
  /** Az új leírás. */
  readonly description: string
  /** Az új cím. */
  readonly title: string
  /** A célplatform. */
  readonly platform: TAvailablePlatform
}

export interface IBaseRouteModule {
  readonly moduleId: number
  readonly routeId: number
  readonly orderNum: number
}

export type TBaseRouteModules = readonly IBaseRouteModule[]

export interface IRouteModule extends IBaseRouteModule {
  readonly createdAt: string
}

export type TRouteModules = readonly IRouteModule[]

export interface IRouteModuleWithAppId extends IRouteModule {
  readonly appId: number
}

export type TRouteModulesWithAppIds = IRouteModuleWithAppId[]

/** App azonosító alapján visszaadott modulok, lapozóval. */
export interface IGetModulesByAppIdResponse extends IPaginationResponse<TModules> {}

/** A felhasználó kedvenc moduljai, lapozóval. */
export interface IGetUserFavouriteModulesReponse extends IPaginationResponse<TModules> {}

/** Cimkék és egyéb keresési feltételek alapján visszaadott modulok, lapozóval.  */
export interface IGetModulesBySearchResponse extends IPaginationResponse<TModules> {}

/** Modulazonosító lista alapján visszaadott modulok, lapozóval. */
export interface IGetModulesByModuleIdsResponse extends IPaginationResponse<TModules> {}

/**
 * Egy megadott felhasználóhoz az AI által generált modulok listája.
 * Jelenleg fixen 10-et generálunk, de később ez változhat, ezért már most megy a lapozó is.
 */
export interface IGetModulesByUserIdentifier extends IPaginationResponse<TModules> {}

export interface IGetModuleBySerialAndLevelOptionsResponse {
  readonly module: IModule
}

export interface IGetModulesDetailsRequest {
  readonly ids: readonly number[]
}

export interface IGetModulesDetailsResponse {
  readonly modules: TMinimalModules
  readonly translations: TInsertModuleTranslations
  readonly labels: TModuleLabels
  readonly images: TModuleImages
}

export interface IGetRandomModulesByThemeAndGradeRequest {
  readonly themeLabelId: number
  readonly grade: TChildrenGrade
}

export interface IModularInitRequest {
  /** Az indítandó app szériaszáma. */
  readonly serial: TSerial
  /** Az indítandó modul azonosítója. */
  readonly moduleId: number
}

export interface IGenerateRecommendableModulesResponse {
  /** Az AI által generált modulok tömbje. */
  readonly modules: TModules
  /** A gyerek adatai, akinek generáltattuk a modulokat. */
  readonly child: IChild
}

export interface ISaveAndSendGeneratedRouteRequest {
  readonly moduleIds: readonly number[]
}

/** Modul-szintopciók. */
export interface IModuleLevelOption {
  readonly moduleId: number
  readonly levelOptionId: number
}

export type TModuleLevelOptions = readonly IModuleLevelOption[]

/** Modulkereső típusai, válaszai. */
export interface IModuleFinderSearchRequest {
  /** Kiválasztott címkék azonosítói. */
  readonly labelIds: number[]
  /** A kulcsszó. */
  readonly keyword: string | undefined
  /** Modulazonosító. */
  readonly moduleId: number | undefined
}

export interface IGetSearchPreviewResponse {
  /** Hány olyan modul van, amely kielégíti az adott keresési feltételeket. */
  readonly moduleCount: number
  /** Címkeazonosítók, amelyek még kiválaszthatóak a jelenlegi keresési feltételt. */
  readonly possibleLabelIds: readonly number[]
}

export interface IModuleFinderPaginationSearchRequest extends IModuleFinderSearchRequest, IPaginationRequest {}

/** Modulkedvencelés. */
export interface IIsFavouriteRequest {
  readonly moduleId: number
}

export interface IToggleFavouriteRequest extends IIsFavouriteRequest {}

/**
 * Ez mondja meg, hogy az adott modul esetén már kedvencnek van-e jelölve, nincs, vagy egyáltalán jelölhet-e kedvencnek.
 * Bárminemű változtatás esetén a összes appot újra kell releaselni!
 */
export enum EIsFavourite {
  True            = 0,
  False           = 1,
  HasNoPermission = 2
}

export interface IIsFavouriteResponse {
  readonly isFavourite: EIsFavourite
}

export interface IToggleFavouriteResponse extends IIsFavouriteResponse {}

/** Adminok számára áttekintő nézet a modulokról. */
export interface IAdminModule extends TExpandedAvailabilities {
  /** A modul azonosítója. */
  readonly id: number
  /** Az app szériaszáma. */
  readonly appSerial: TSerial // TODO: app név!
  // TODO: be kellene nyelvesíteni!

  /** A célplatformon elérhető név és leírás. */
  readonly title: string | null
  readonly description: string | null

  /** A célplatformon elérhető kép. */
  readonly imagePath: string | null

  /** Ha van neki elsődleges szintopciója, akkor annak kulcs+érték. */
  readonly primaryLevelOptionKey: string | null
  readonly primaryLevelOptionValue: string | null
}

export type TAdminModules = readonly IAdminModule[]

export interface IGetModulesRequest extends IPaginationRequest {
  /** App-ra való szűkítés lehetősége. */
  readonly appId: number

  /** Elérhetőségre való szűkítés. */
  readonly platform: TAvailablePlatform
}

export interface IGetModulesResponse extends IPaginationResponse<TAdminModules> {}

/**
 * A modul elérhetőségének beállításához használt típus, amely segít több legyet egy csapásra ütni.
 * Megmondja, hogy a megadott elérhetőséget milyen szinten kell beállítani.
 */
export const MODULE_AVAILABILITY_SCOPES = [
  /** Csak és kizárólag a kiválasztott modul. */
  'onlyModule',
  /** A modul tartalmazó teljes app minden egyéb modulja is. */
  'wholeApp',
  /** Csak azon modulok, amelyeknek pontosan megegyeznek a szintopciói a kiválaszott modullal. */
  'matchingLevelOptions',
  /**
   * Csak azon modulok, amelyeknek pontosan megegyezik az
   * elsődleges szintopciója a kiválaszott modullal.
   */
  'matchingPrimaryLevelOptions'
] as const

export type TModuleAvailabilityScope = typeof MODULE_AVAILABILITY_SCOPES[number]

export interface ISetModuleAvailabilitiesRequest {
  /** Az eredetileg kiválasztott modul azonosítója. */
  readonly moduleId: number
  /** A beállítás hatásköre. */
  readonly scope: TModuleAvailabilityScope
  /** A kiválasztott platformok. */
  readonly selectedPlatforms: readonly TModuleablePlatform[]
}

export interface IGetModuleByIdRequest {
  /** Melyik platformhoz tartozó adatok érdekelnek. */
  readonly platform: TAvailablePlatform
}
